import { Component, ErrorHandler, OnDestroy, OnInit, ViewChild, ChangeDetectionStrategy, signal } from '@angular/core'
import { MatButtonToggleGroup } from '@angular/material/button-toggle'
import { MatDialog, MatDialogConfig } from '@angular/material/dialog'
import { MatSnackBar } from '@angular/material/snack-bar'
import { Laskuasetukset } from '@lemon/core-firebase/model/lasku'
import { LaskuKopioija } from '@lemon/core-firebase/service/lasku/lasku.kopioija'
import { AsetustenOletuksetService } from 'app/_angular/service/asetusten-oletukset.service'
import { FirebaseLemonaid } from 'app/_angular/service/firebase-lemonaid.service'
import { KayttajaService } from 'app/_angular/service/kayttaja.service'
import { LaskutusprofiiliService, LaskutusprofiiliVaihtoehto } from 'app/_angular/service/lasku/laskutusprofiili.service'
import { UserSettingsService } from 'app/_angular/service/user-settings.service'
import { AreYouSureDialog, AreYouSureDialogData } from 'app/_jaettu-angular/_components/are-you-sure.dialog'
import { LadataanService } from 'app/_jaettu-angular/service/ladataan.service'
import { LemonTranslationService } from 'app/_jaettu-angular/service/lemon-translation.service'
import { EmailPohjaComponent } from 'app/laskut/asetukset/emailpohja.component'
import { LaskuasetuksetComponent } from 'app/laskut/asetukset/laskuasetukset.component'
import { PerusasetuksetComponent } from 'app/laskut/asetukset/perusasetukset.component'
import { LaskuTaydennaAsetuksetDialog } from 'app/laskut/dialogit/lasku-taydenna-asetukset.dialog'
import { combineLatest, firstValueFrom, Subject } from 'rxjs'
import { takeUntil } from 'rxjs/operators'
import { LaskutusprofiiliLisaaDialog, LaskutusprofiiliLisaaDialogData } from './laskutusprofiili-lisaa.dialog'

interface LaskutusprofiilinValilehti {
  onkoTallentamattomiaMuutoksia(): boolean
}

@Component({
  selector: 'app-asetukset',
  templateUrl: './asetukset.component.html',
  styleUrls: ['./asetukset.component.css'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AsetuksetComponent implements OnInit, OnDestroy {
  @ViewChild('perusasetukset') perusasetukset: PerusasetuksetComponent
  @ViewChild('ulkoasu') ulkoasu: LaskuasetuksetComponent
  @ViewChild('maksutilit') maksutilit: LaskuasetuksetComponent
  @ViewChild('oletukset') oletukset: LaskuasetuksetComponent
  @ViewChild('emailpohja') emailpohja: EmailPohjaComponent

  readonly laskutusprofiilit = signal<LaskutusprofiiliVaihtoehto[]>([])
  readonly valittuLaskutusprofiili = signal<Laskuasetukset | null>(null)
  readonly oletusprofiiliValittu = signal(true)
  readonly valittuValilehti = signal(0)

  private _ngUnsubscribe = new Subject<void>()

  // showNewFeatureSignObservable: Observable<boolean>
  constructor(
    private _userSettingsService: UserSettingsService,
    private _kayttajaService: KayttajaService,
    private _lemonaidFirebase: FirebaseLemonaid,
    private _laskutusprofiiliService: LaskutusprofiiliService,
    private _dialog: MatDialog,
    private _snackbar: MatSnackBar,
    private _ladataanService: LadataanService,
    private _translationService: LemonTranslationService,
    private _errorHandler: ErrorHandler,
    private _asetustenOletuksetService: AsetustenOletuksetService,
    private _laskuKopioija: LaskuKopioija,
  ) {}
  ngOnInit(): void {
    this._laskutusprofiiliService.valitseLaskutusprofiili(null)

    combineLatest([this._laskutusprofiiliService.laskutusprofiiliVaihtoehdotObservable, this._laskutusprofiiliService.valittuLaskutusprofiiliObservable])
      .pipe(takeUntil(this._ngUnsubscribe))
      .subscribe(([vaihtoehdot, valittuLaskutusprofiili]) => {
        this.laskutusprofiilit.set(vaihtoehdot)
        this.valittuLaskutusprofiili.set(valittuLaskutusprofiili)
        const valittuVaihtoehto = vaihtoehdot.find(vaihtoehto => vaihtoehto.avain === valittuLaskutusprofiili?.avain)
        this.oletusprofiiliValittu.set(valittuVaihtoehto?.oletusprofiili ?? true)
      })

    // this.showNewFeatureSignObservable = this._userSettingsService.userSettingsObservable.pipe(
    //   map(newFeatures => {
    //     if (!newFeatures?.seen[NewFeature.AUTOMAATTINEN_RESKONTRA]) {
    //       return true
    //     }
    //     return false
    //   })
    // )
  }

  ngOnDestroy() {
    this._ngUnsubscribe.next()
    this._ngUnsubscribe.complete()
  }

  async vaihdaLaskutusprofiili(laskutusprofiiliAvain: string, laskutusprofiiliValitsin: MatButtonToggleGroup) {
    const voidaanVaihtaa = await this._hylataankoTallentamattomatMuutokset()
    if (!voidaanVaihtaa) {
      laskutusprofiiliValitsin.value = this.valittuLaskutusprofiili()?.avain
      return
    }
    this._laskutusprofiiliService.valitseLaskutusprofiili(laskutusprofiiliAvain)
  }

  async lisaaLaskutusprofiili() {
    const voidaanVaihtaa = await this._hylataankoTallentamattomatMuutokset()
    if (!voidaanVaihtaa || !this.valittuLaskutusprofiili()) {
      return
    }

    const oletusprofiiliTaydennetty = await this._taydennaOletusprofiilinPuuttuvatAsetukset()
    if (!oletusprofiiliTaydennetty) {
      return
    }

    const lahdeprofiili = await this._laskutusprofiiliService.annaLaskutusprofiili(this.valittuLaskutusprofiili().avain)
    if (!lahdeprofiili) {
      return
    }

    const data: LaskutusprofiiliLisaaDialogData = { lahdeprofiilinNimi: this._laskutusprofiiliService.annaLaskutusprofiilinNimi(lahdeprofiili) }
    const laskutusprofiilinNimi = await firstValueFrom(this._dialog.open<LaskutusprofiiliLisaaDialog, LaskutusprofiiliLisaaDialogData, string>(LaskutusprofiiliLisaaDialog, { data }).afterClosed())
    if (!laskutusprofiilinNimi) {
      return
    }

    this._ladataanService.aloitaLataaminen()
    try {
      const uusiLaskutusprofiiliAvain = await this._laskutusprofiiliService.luoLaskutusprofiili(lahdeprofiili, laskutusprofiilinNimi)
      this._laskutusprofiiliService.valitseLaskutusprofiili(uusiLaskutusprofiiliAvain)
      this.valittuValilehti.set(0)
      this._naytaIlmoitus('asetukset.laskutusprofiili-luotu')
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  private async _taydennaOletusprofiilinPuuttuvatAsetukset(): Promise<boolean> {
    const oletusprofiili = await this._laskutusprofiiliService.annaLaskutusprofiili(undefined)
    if (!oletusprofiili) {
      return false
    }
    const tarkistettavaOletusprofiili = this._laskuKopioija.copyAsetukset(oletusprofiili)
    const asetuksiaPuuttuu = this._asetustenOletuksetService.tarkistaPuuttuukoLaskuasetuksia(tarkistettavaOletusprofiili)
    if (!asetuksiaPuuttuu) {
      return true
    }

    const kaytettavaLeveys = window.innerWidth
    const kaytettavaKorkeus = window.innerHeight
    const leveys = kaytettavaLeveys > 900 ? '90vw' : '98vw'
    const korkeus = kaytettavaKorkeus > 1100 ? '80vh' : kaytettavaKorkeus > 750 ? '85vh' : '92vh'
    const settings: MatDialogConfig = {
      maxWidth: '900px',
      maxHeight: korkeus,
      width: leveys,
      data: {
        mobiili: kaytettavaLeveys < 670,
      },
    }
    const tulos = await firstValueFrom(this._dialog.open(LaskuTaydennaAsetuksetDialog, settings).afterClosed())
    return tulos === 'onnistui'
  }

  async poistaLaskutusprofiili() {
    if (!this.valittuLaskutusprofiili() || this.oletusprofiiliValittu()) {
      return
    }

    const laskutusprofiilinNimi = this._laskutusprofiiliService.annaLaskutusprofiilinNimi(this.valittuLaskutusprofiili())
    const oletusprofiili = this.laskutusprofiilit().find(laskutusprofiili => laskutusprofiili.oletusprofiili)
    const oletusprofiilinNimi = oletusprofiili?.nimi ?? ''
    const data: AreYouSureDialogData = {
      header: this._translationService.lokalisoi('asetukset.poista-laskutusprofiili'),
      text: this._translationService.lokalisoi('asetukset.poista-laskutusprofiili-dialog.teksti', { laskutusprofiili: laskutusprofiilinNimi, oletusprofiili: oletusprofiilinNimi }),
      rightAction: this._translationService.lokalisoi('asetukset.poista-laskutusprofiili-dialog.poista'),
    }
    const poistetaan = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data }).afterClosed())
    if (!poistetaan) {
      return
    }

    this._ladataanService.aloitaLataaminen()
    try {
      await this._laskutusprofiiliService.poistaLaskutusprofiili(this.valittuLaskutusprofiili().avain)
      this._laskutusprofiiliService.valitseLaskutusprofiili(null)
      this._naytaIlmoitus('asetukset.laskutusprofiili-poistettu')
    } catch (err) {
      this._errorHandler.handleError(err)
    } finally {
      this._ladataanService.lopetaLataaminen()
    }
  }

  private async _hylataankoTallentamattomatMuutokset(): Promise<boolean> {
    const avoinValilehti = this._annaAvoinValilehti()
    const tallentamattomiaMuutoksia = avoinValilehti?.onkoTallentamattomiaMuutoksia() ?? false
    if (!tallentamattomiaMuutoksia) {
      return true
    }

    const laskutusprofiilinNimi = this._laskutusprofiiliService.annaLaskutusprofiilinNimi(this.valittuLaskutusprofiili())
    const data: AreYouSureDialogData = {
      header: this._translationService.lokalisoi('asetukset.tallentamattomat-muutokset.otsikko'),
      text: this._translationService.lokalisoi('asetukset.tallentamattomat-muutokset.teksti', { laskutusprofiili: laskutusprofiilinNimi }),
      rightAction: this._translationService.lokalisoi('asetukset.tallentamattomat-muutokset.vaihda'),
    }
    const hylataan = await firstValueFrom(this._dialog.open(AreYouSureDialog, { data }).afterClosed())
    return !!hylataan
  }

  private _annaAvoinValilehti(): LaskutusprofiilinValilehti | undefined {
    const laskutusprofiilinValilehdet: LaskutusprofiilinValilehti[] = [this.perusasetukset, this.ulkoasu, this.maksutilit, this.oletukset, this.emailpohja]
    return laskutusprofiilinValilehdet[this.valittuValilehti()]
  }

  private _naytaIlmoitus(kaannosavain: string) {
    const ilmoitus = this._translationService.lokalisoi(kaannosavain)
    this._snackbar.open(ilmoitus, 'OK', { duration: 5000, verticalPosition: 'top' })
  }

  // tabChanged(event: MatTabChangeEvent) {
  //   if (event.index === 3) {
  //     firstValueFrom(this.showNewFeatureSignObservable).then(isShown => {
  //       if (!isShown) {
  //         return
  //       }
  //       return firstValueFrom(this._kayttajaService.kayttajanTiedotObservable).then(kayttajanTiedot => {
  //         if (kayttajanTiedot) {
  //           return this._userSettingsService.setFeatureSeen(NewFeature.AUTOMAATTINEN_RESKONTRA, kayttajanTiedot.asiakasAvain, kayttajanTiedot.uid, this._lemonaidFirebase)
  //         }
  //       })
  //     })
  //   }
  // }
}
